/* eslint-disable */
import * as React from 'react';
import {
  Badge, Box, Button, Collapse, Flex, FormControl, FormLabel, HStack, Icon, IconButton,
  Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter,
  ModalOverlay, Stack, Table, Tbody, Td, Text, Th, Thead, Tooltip,
  Tr, useColorModeValue, useDisclosure, useToast,
} from '@chakra-ui/react';
import {
  ColumnFiltersState, PaginationState, createColumnHelper, flexRender,
  getCoreRowModel, getFacetedMinMaxValues, getFacetedRowModel, getFacetedUniqueValues,
  getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { MdAdminPanelSettings, MdCancel, MdChevronLeft, MdChevronRight, MdDoneAll, MdFilterList, MdLockReset, MdRefresh } from 'react-icons/md';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';

type RowObj = {
  id: number; NombreCompleto: string; CorreoElectronico: string;
  Bloqueado: boolean; createdAt: string;
};

const inputStyles = {
  variant: 'filled' as const,
  borderRadius: 'lg',
  _focus: { bg: 'white', borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' },
};

export default function SearchTableOrders(props: { tableData: any }) {
  const { tableData } = props;
  const toast = useToast();
  const filterBg = useColorModeValue('gray.50', 'navy.900');
  const textColor = useColorModeValue('navy.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const brandColor = useColorModeValue('brand.500', 'brand.400');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');

  // password reset modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [orden, setOrden] = React.useState(0);
  const [password, setPassword] = React.useState('');

  // nuevo admin modal
  const { isOpen: isOpenNuevo, onOpen: onOpenNuevo, onClose: onCloseNuevo } = useDisclosure();
  const [showFilters, setShowFilters] = React.useState(false);
  const [estatusFilter, setEstatusFilter] = React.useState<'Todos' | 'Activo' | 'Bloqueado'>('Todos');
  const [fechaDesde, setFechaDesde] = React.useState('');
  const [fechaHasta, setFechaHasta] = React.useState('');

  const activeFilters = [estatusFilter !== 'Todos', !!(fechaDesde || fechaHasta)].filter(Boolean).length;
  const resetFilters = () => { setEstatusFilter('Todos'); setFechaDesde(''); setFechaHasta(''); };

  const [data, setData] = React.useState<RowObj[]>(() => [...tableData]);
  const [updatingId, setUpdatingId] = React.useState<number | null>(null);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [{ pageIndex, pageSize }, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize: 8 });
  const pagination = React.useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

  const filteredData = React.useMemo(() => data.filter((r) => {
    if (estatusFilter === 'Activo' && r.Bloqueado) return false;
    if (estatusFilter === 'Bloqueado' && !r.Bloqueado) return false;
    if (fechaDesde || fechaHasta) {
      const d = r.createdAt ? new Date(r.createdAt) : null;
      if (!d) return false;
      if (fechaDesde && d < new Date(fechaDesde)) return false;
      if (fechaHasta && d > new Date(fechaHasta + 'T23:59:59')) return false;
    }
    return true;
  }), [data, estatusFilter, fechaDesde, fechaHasta]);

  const counts = React.useMemo(() => ({
    total: data.length,
    activos: data.filter(r => !r.Bloqueado).length,
    bloqueados: data.filter(r => r.Bloqueado).length,
  }), [data]);

  const { handleSubmit, register, reset, formState: { isSubmitting } } = useForm();

  const refreshTable = async () => {
    const d = await fetch(process.env.REACT_APP_URL_SERVER + '/admins', { credentials: 'include' }).then(r => r.json()).catch(() => null);
    if (d) setData(d);
  };

  const onSubmitNuevo = async (values: any) => {
    const fd = new FormData();
    for (const k in values) fd.append(k, values[k]);
    const res = await fetch(process.env.REACT_APP_URL_SERVER + '/new_admin', { method: 'POST', body: fd, credentials: 'include' });
    if (res.ok) { toast({ title: 'Administrador registrado', status: 'success', duration: 3000 }); reset(); onCloseNuevo(); await refreshTable(); }
    else toast({ title: 'Error al guardar', status: 'error', duration: 3000 });
  };

  const updateAdminStatus = async (id: number, bloqueado: boolean) => {
    setUpdatingId(id);
    try {
      const fd = new FormData();
      fd.append('id', String(id));
      fd.append('Bloqueado', String(bloqueado));

      const res = await fetch(process.env.REACT_APP_URL_SERVER + '/toggle_admin_status', {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });

      if (!res.ok) throw new Error('No fue posible actualizar el administrador.');

      setData((current) =>
        current.map((item) =>
          item.id === id ? { ...item, Bloqueado: bloqueado } : item,
        ),
      );

      toast({
        title: bloqueado ? 'Administrador bloqueado' : 'Administrador activado',
        status: 'success',
        duration: 2500,
      });
    } catch (error) {
      toast({
        title: 'Error al actualizar',
        description: error instanceof Error ? error.message : 'No se pudo cambiar el estatus.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const columnHelper = createColumnHelper<RowObj>();
  const columns = [
    columnHelper.accessor('NombreCompleto', {
      id: 'nombre',
      header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">ADMINISTRADOR</Text>,
      cell: (info) => <Box><Text color={textColor} fontWeight="600" fontSize="sm">{info.getValue()}</Text><Text color="gray.400" fontSize="xs">{info.row.original.CorreoElectronico}</Text></Box>,
    }),
    columnHelper.accessor('createdAt', {
      id: 'registro',
      header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">REGISTRO</Text>,
      cell: (info) => { const v = info.getValue(); return <Text color="gray.500" fontSize="xs">{v ? new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</Text>; },
    }),
    columnHelper.accessor('Bloqueado', {
      id: 'estatus',
      header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">ESTATUS</Text>,
      cell: (info) => <Badge colorScheme={info.getValue() ? 'red' : 'green'} fontSize="xs" borderRadius="full" px="3" py="1">{info.getValue() ? 'Bloqueado' : 'Activo'}</Badge>,
    }),
    columnHelper.accessor('id', {
      id: 'actions',
      header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">ACCIONES</Text>,
      cell: (info) => (
        <HStack spacing="2px">
          <Tooltip label="Activar"><IconButton aria-label="activar" size="sm" variant="ghost" colorScheme="green" icon={<Icon as={MdDoneAll} h="18px" w="18px" />} isDisabled={!info.row.original.Bloqueado || updatingId === info.getValue()} isLoading={updatingId === info.getValue() && info.row.original.Bloqueado} onClick={() => updateAdminStatus(info.getValue(), false)} /></Tooltip>
          <Tooltip label="Bloquear"><IconButton aria-label="bloquear" size="sm" variant="ghost" colorScheme="red" icon={<Icon as={MdCancel} h="18px" w="18px" />} isDisabled={info.row.original.Bloqueado || updatingId === info.getValue()} isLoading={updatingId === info.getValue() && !info.row.original.Bloqueado} onClick={() => updateAdminStatus(info.getValue(), true)} /></Tooltip>
          <Tooltip label="Cambiar contraseña"><IconButton aria-label="password" size="sm" variant="ghost" colorScheme="blue" icon={<Icon as={MdLockReset} h="18px" w="18px" />} onClick={() => { setOrden(info.getValue()); onOpen(); }} /></Tooltip>
        </HStack>
      ),
    }),
  ];

  const table = useReactTable({
    data: filteredData, columns,
    state: { columnFilters, globalFilter, pagination },
    onPaginationChange: setPagination, onColumnFiltersChange: setColumnFilters, onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(), getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(), getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(), getFacetedUniqueValues: getFacetedUniqueValues(), getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });
  const createPages = (n: number) => Array.from({ length: n }, (_, i) => i + 1);

  return (
    <Flex direction="column" w="100%" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
      <Flex align="center" justify="space-between" w="100%" px="22px" mb="16px" flexWrap="wrap" gap="10px">
        <DebouncedInput value={globalFilter ?? ''} onChange={(v) => setGlobalFilter(String(v))} placeholder="Buscar administrador..." />
        <HStack spacing="8px">
          <Tooltip label="Actualizar"><IconButton aria-label="refresh" size="md" variant="outline" icon={<Icon as={MdRefresh} h="18px" w="18px" />} onClick={refreshTable} /></Tooltip>
          <Button variant="outline" fontSize="sm" leftIcon={<Icon as={MdFilterList} />} onClick={() => setShowFilters(v => !v)} position="relative">
            Filtros{activeFilters > 0 && <Badge colorScheme="brand" borderRadius="full" position="absolute" top="-6px" right="-6px" fontSize="10px" minW="18px" h="18px" display="flex" alignItems="center" justifyContent="center">{activeFilters}</Badge>}
          </Button>
          <Button variant="brand" fontSize="sm" fontWeight="500" onClick={onOpenNuevo}>+ Nuevo Admin</Button>
        </HStack>
      </Flex>

      {/* Summary chips */}
      <Flex px="22px" mb="12px" gap="8px" flexWrap="wrap">
        {[{ label: 'Todos', val: 'Todos' as const, count: counts.total, color: 'gray' }, { label: 'Activo', val: 'Activo' as const, count: counts.activos, color: 'green' }, { label: 'Bloqueado', val: 'Bloqueado' as const, count: counts.bloqueados, color: 'red' }].map(f => (
          <Badge key={f.val} colorScheme={estatusFilter === f.val ? f.color : 'gray'} variant={estatusFilter === f.val ? 'solid' : 'outline'} fontSize="xs" px="3" py="1" borderRadius="full" cursor="pointer" onClick={() => { setEstatusFilter(f.val); setPagination(p => ({ ...p, pageIndex: 0 })); }} _hover={{ opacity: 0.8 }} userSelect="none">{f.label} ({f.count})</Badge>
        ))}
        {activeFilters > 0 && <Badge colorScheme="orange" variant="outline" fontSize="xs" px="3" py="1" borderRadius="full" cursor="pointer" onClick={resetFilters} _hover={{ opacity: 0.8 }} userSelect="none">✕ Limpiar</Badge>}
      </Flex>

      <Collapse in={showFilters} animateOpacity>
        <Box mx="22px" mb="16px" p="16px" bg={filterBg} borderRadius="12px" border="1px solid" borderColor={borderColor}>
          <Text fontSize="sm" fontWeight="700" color={textColor} mb="14px">Filtros avanzados</Text>
          <Box>
            <FormLabel fontSize="xs" fontWeight="600" color="gray.500" mb="6px">FECHA DE REGISTRO</FormLabel>
            <HStack spacing="8px">
              <Input size="sm" type="date" borderRadius="8px" value={fechaDesde} onChange={(e) => { setFechaDesde(e.target.value); setPagination(p => ({ ...p, pageIndex: 0 })); }} />
              <Text fontSize="xs" color="gray.400">al</Text>
              <Input size="sm" type="date" borderRadius="8px" value={fechaHasta} onChange={(e) => { setFechaHasta(e.target.value); setPagination(p => ({ ...p, pageIndex: 0 })); }} />
            </HStack>
          </Box>
          <Flex mt="14px" justify="flex-end"><Button size="sm" variant="ghost" colorScheme="red" onClick={resetFilters}>Limpiar</Button></Flex>
        </Box>
      </Collapse>
      {activeFilters > 0 && <Text px="22px" mb="8px" fontSize="xs" color="gray.500">Mostrando <strong>{filteredData.length}</strong> de {data.length}</Text>}

      {/* Password change modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside" motionPreset="slideInBottom">
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.400" />
        <ModalContent borderRadius="2xl" overflow="hidden" mx={4}>
          <Box bg="blue.500" px={6} py={4}>
            <HStack justify="space-between" align="center">
              <HStack gap={3}>
                <Icon as={MdLockReset} color="white" w={5} h={5} />
                <Text color="white" fontWeight="700" fontSize="lg">Cambiar contraseña — Admin #{orden}</Text>
              </HStack>
              <ModalCloseButton position="static" color="white" _hover={{ bg: 'whiteAlpha.300' }} borderRadius="lg" />
            </HStack>
          </Box>
          <ModalBody px={6} py={5}>
            <FField label="Nueva contraseña">
              <Input
                id="passwordx"
                placeholder="p4s5w0rd!"
                onChange={(e) => setPassword(e.target.value)}
                {...inputStyles}
              />
            </FField>
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3} px={6} py={4}>
            <Button variant="ghost" borderRadius="xl" onClick={onClose}>Cerrar</Button>
            <Button colorScheme="blue" borderRadius="xl" onClick={async () => {
              await fetch(process.env.REACT_APP_URL_SERVER + '/ch4nge_password', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: orden, pass: password }), credentials: 'include' });
              toast({ title: 'Contraseña actualizada', status: 'success', duration: 2000 });
              onClose();
            }}>Cambiar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Nuevo admin modal */}
      <Modal isOpen={isOpenNuevo} onClose={onCloseNuevo} size="2xl" scrollBehavior="inside" motionPreset="slideInBottom">
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.400" />
        <ModalContent borderRadius="2xl" overflow="hidden" mx={4}>
          <Box bg="brand.500" px={6} py={4}>
            <HStack justify="space-between" align="center">
              <HStack gap={3}>
                <Icon as={MdAdminPanelSettings} color="white" w={5} h={5} />
                <Text color="white" fontWeight="700" fontSize="lg">Registrar Nuevo Administrador</Text>
              </HStack>
              <ModalCloseButton position="static" color="white" _hover={{ bg: 'whiteAlpha.300' }} borderRadius="lg" />
            </HStack>
          </Box>
          <form onSubmit={handleSubmit(onSubmitNuevo)}>
            <ModalBody px={6} py={5}>
              <Stack gap="14px">
                <FField label="Nombre Completo">
                  <Input placeholder="Carlos Admin" {...register('NombreCompleto', { required: true })} {...inputStyles} />
                </FField>
                <FField label="Correo Electrónico">
                  <Input type="email" placeholder="admin@sistema.mx" {...register('CorreoElectronico', { required: true })} {...inputStyles} />
                </FField>
                <FField label="Contraseña">
                  <Input type="password" placeholder="••••••••" {...register('Password', { required: true })} {...inputStyles} />
                </FField>
              </Stack>
            </ModalBody>
            <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3} px={6} py={4}>
              <Button variant="ghost" borderRadius="xl" onClick={onCloseNuevo}>Cancelar</Button>
              <Button colorScheme="brand" type="submit" isLoading={isSubmitting} borderRadius="xl" px={8}>Guardar</Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      <Table variant="simple" color="gray.500" mb="24px">
        <Thead>{table.getHeaderGroups().map(hg => (<Tr key={hg.id}>{hg.headers.map(h => (<Th pe="10px" borderColor={borderColor} key={h.id} colSpan={h.colSpan}>{h.isPlaceholder ? null : (<Flex justify="space-between" align="center" fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" cursor={h.column.getCanSort() ? 'pointer' : 'default'} onClick={h.column.getToggleSortingHandler()}>{flexRender(h.column.columnDef.header, h.getContext())}</Flex>)}</Th>))}</Tr>))}</Thead>
        <Tbody>
          {table.getRowModel().rows.length === 0 ? (<Tr><Td colSpan={columns.length} textAlign="center" py="40px" color="gray.400">Sin registros</Td></Tr>) :
            table.getRowModel().rows.map(row => (<Tr key={row.id} _hover={{ bg: rowHover }}>{row.getVisibleCells().map(cell => (<Td key={cell.id} fontSize={{ sm: '14px' }} minW={{ sm: '120px', md: 'auto' }} borderColor={borderColor} py="12px">{flexRender(cell.column.columnDef.cell, cell.getContext())}</Td>))}</Tr>))}
        </Tbody>
      </Table>
      <Flex w="100%" justify="space-between" px="20px" pt="10px" pb="5px">
        <Text fontSize="sm" color="gray.500" mb={{ sm: '24px', md: '0px' }}>{filteredData.length === 0 ? 'Sin registros' : `${pageSize * pageIndex + 1}–${Math.min(pageSize * (pageIndex + 1), filteredData.length)} de ${filteredData.length}`}</Text>
        <Stack direction="row" alignSelf="flex-end" spacing="4px" ms="auto">
          <Button variant="no-effects" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} w="40px" h="40px" borderRadius="50%" bg="transparent" border="1px solid" borderColor={borderColor} display={table.getCanPreviousPage() ? 'flex' : 'none'} _hover={{ opacity: '0.7' }}><Icon as={MdChevronLeft} w="16px" h="16px" color={textColor} /></Button>
          {createPages(table.getPageCount()).map((p, i) => (<Button key={i} variant="no-effects" onClick={() => table.setPageIndex(p - 1)} w="40px" h="40px" borderRadius="50%" bg={p === pageIndex + 1 ? brandColor : 'transparent'} border={p === pageIndex + 1 ? 'none' : '1px solid lightgray'} _hover={p === pageIndex + 1 ? { opacity: '0.7' } : { bg: 'whiteAlpha.100' }}><Text fontSize="sm" color={p === pageIndex + 1 ? '#fff' : textColor}>{p}</Text></Button>))}
          <Button variant="no-effects" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} w="40px" h="40px" borderRadius="50%" bg="transparent" border="1px solid" borderColor={borderColor} display={table.getCanNextPage() ? 'flex' : 'none'} _hover={{ opacity: '0.7' }}><Icon as={MdChevronRight} w="16px" h="16px" color={textColor} /></Button>
        </Stack>
      </Flex>
    </Flex>
  );
}

function FField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <FormControl>
      <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wide" color="gray.500" mb={1}>{label}</FormLabel>
      {children}
    </FormControl>
  );
}

function DebouncedInput({ value: iv, onChange, debounce = 500, ...props }: { value: string | number; onChange: (v: string | number) => void; debounce?: number } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = React.useState(iv);
  React.useEffect(() => { setValue(iv); }, [iv]);
  React.useEffect(() => { const t = setTimeout(() => onChange(value), debounce); return () => clearTimeout(t); }, [value]);
  return <SearchBar {...props} value={value} onChange={(e: any) => setValue(e.target.value)} h="44px" w={{ lg: '390px' }} borderRadius="16px" />;
}
